import { useCallback } from 'react'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { useTraderRoomRoutesConditions } from '@pages/Traders-Room/TradersRoom.routes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { isKYCUpdateRequired } from '@utils/AccountAccess/accountKYCStatuses'
import { CommonPaths } from '@utils/path'

import {
  CFDLandingPageID,
  ETDLandingPageID,
  GenericLandingPageID,
  LandingPageMapping,
} from '@model/LandingPageDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useDocumentVerificationPath } from './useDocumentVerificationPath'

export const useLandingPage = (): {
  getLandingPage: (overWriteProduct?: TickmillProductType) => string
} => {
  const { account } = useAccountReadContext()
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const { isIBVisible } = useIBRoutesConditions()
  const { product } = useProductReadContext()
  const docVerificationPath = useDocumentVerificationPath()

  const getLandingPage = useCallback(
    (overWriteProduct?: TickmillProductType) => {
      const tMProduct = overWriteProduct ?? product

      const DEFAULT_CFD_PAGE = CommonPaths.TradersRoomWallets
      const DEFAULT_CFD_IB_PAGE = CommonPaths.TradersRoomWallets

      const KYC_UPDATE_PAGE = CommonPaths.KycUpdateDetails
      const DEFAULT_ETD_PAGE = CommonPaths.TradersRoomBalances

      if (isKYCUpdateRequired(account)) {
        return KYC_UPDATE_PAGE
      }

      if (
        !account?.preferredLandingPage ||
        account?.preferredLandingPage.tickmillProductId !== tMProduct
      ) {
        if (tMProduct === TickmillProductType.CFD) {
          if (isTradersRoomVisible) {
            return DEFAULT_CFD_PAGE
          } else {
            if (isIBVisible) {
              return DEFAULT_CFD_IB_PAGE
            } else {
              return docVerificationPath
            }
          }
        }
        return DEFAULT_ETD_PAGE
      }

      const preferredLandingPage: ETDLandingPageID | CFDLandingPageID | GenericLandingPageID =
        account.preferredLandingPage.id
      return LandingPageMapping[preferredLandingPage]
    },
    [account, docVerificationPath, isIBVisible, isTradersRoomVisible, product]
  )

  return {
    getLandingPage,
  }
}
